/* Where a signed-out visitor was trying to go, so login can take them there.

   A protected link opened from an email (e.g. the institution contract
   "Review, sign and pay" button) bounces through /auth — and sometimes
   through Google OAuth or /setup-password, which both drop the query string.
   The intended path is kept in sessionStorage (same tab, short-lived) so it
   survives those hops; /auth also accepts it as ?redirect=. */

const KEY = "post_auth_redirect";
const MAX_AGE_MS = 30 * 60_000;

// Pages that are never a meaningful destination after logging in.
const IGNORED = ["/auth", "/setup-password", "/account-paused"];

/** Same-site absolute path only — never "//host" or "/\host" (open redirect). */
export function isSafeRedirect(path: string | null | undefined): path is string {
  return !!path && path.startsWith("/") && !path.startsWith("//") && !path.startsWith("/\\");
}

export function rememberPostAuthRedirect(path: string) {
  if (!isSafeRedirect(path) || IGNORED.some((p) => path === p || path.startsWith(`${p}?`))) return;
  // signOut() clears the user a moment before its hard redirect; the page the
  // user just left is not somewhere the next login should land.
  if ((window as any).__iaSigningOut) return;
  try { sessionStorage.setItem(KEY, JSON.stringify({ path, at: Date.now() })); } catch { /* private mode */ }
}

export function peekPostAuthRedirect(): string | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const { path, at } = JSON.parse(raw) as { path?: string; at?: number };
    if (!isSafeRedirect(path) || !at || Date.now() - at > MAX_AGE_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return path;
  } catch {
    return null;
  }
}

export function consumePostAuthRedirect(): string | null {
  const path = peekPostAuthRedirect();
  try { sessionStorage.removeItem(KEY); } catch { /* private mode */ }
  return path;
}
